// ============================================================================
// Canonical Equipment Fueling form route contract
// ============================================================================
//
// This pure pathname predicate is intentionally shared by the generic
// authenticated-surface resolver and the released-render boundary. It contains
// no ledger, renderer, or data-owner knowledge: its only job is to keep the
// one independently reviewed Fueling form from drifting wider than the legacy
// FuelingHub dispatcher, which deliberately accepts the broad `/equipment/*`
// family.
// ============================================================================

export const EQUIPMENT_FUELING_FORM_PATHNAME_PREFIX = '/equipment/';

/**
 * Segments directly under `/equipment/` that are never a machine id.
 *
 * `supply` is the Fuel Supply webform. `fleet` and `fuel-log` are legacy exact
 * aliases that redirect into the `/fleet/*` world, so claiming either would
 * both mis-render the Fleet index as a fueling form and race its redirect.
 * `.` and `..` are traversal tokens.
 *
 * This module stays import-free like its sibling route contracts, so the alias
 * half of this list is not derived from `ALIASES_EXACT` at runtime. It is held
 * honest instead by a static guard that fails loudly the moment a new
 * `/equipment/*` alias is added to `src/lib/routes.ts` — an audited release
 * boundary should widen only under explicit review, never by inheriting a
 * change to the legacy dispatcher's table.
 */
export const EQUIPMENT_FUELING_FORM_RESERVED_SEGMENTS: readonly string[] = Object.freeze([
  'supply',
  'fleet',
  'fuel-log',
  '.',
  '..',
]);

/**
 * Accept exactly one meaningful, non-reserved raw path segment. Decode before
 * final validation so encoded separators, NUL, dot, and reserved segments
 * cannot turn the record capability into a broader legacy route claim.
 */
export function isCanonicalEquipmentFuelingFormPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(EQUIPMENT_FUELING_FORM_PATHNAME_PREFIX)) {
    return false;
  }

  const rawSegment = pathname.slice(EQUIPMENT_FUELING_FORM_PATHNAME_PREFIX.length);
  if (rawSegment === '' || rawSegment.includes('/') || rawSegment.includes('\\') || rawSegment.includes('\0')) {
    return false;
  }

  try {
    const segment = decodeURIComponent(rawSegment);
    return (
      segment.trim() !== '' &&
      !EQUIPMENT_FUELING_FORM_RESERVED_SEGMENTS.includes(segment) &&
      !segment.includes('/') &&
      !segment.includes('\\') &&
      !segment.includes('\0')
    );
  } catch {
    return false;
  }
}
